import { Grid } from "@mui/material";
import { ICommissionCyclesPerMonthFields, ISystemSettings } from "@/Settings/types/Common/model";
import SmartSelector from "@/Layouts/components/InputComponents/SmartSelector";
import { useAuthorization } from "@/Common/hooks/Authorization/Authorization";
import actions from "@/Constants/actionsList";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { updateSystemSettings } from "@/Settings/api/SystemSettings/mutations";
import { errorLogger } from "@/Common/helpers/logger";
import { useSelector } from "react-redux";
import { RootState } from "@/redux/store";

const getSpecificOptions = (number = 1) => Array.from({ length: number }, (_, i) => i + 1).map((key) => ({ key, value: key }));

const getSelectedOption = (option?: ICommissionCyclesPerMonthFields | null) => {
	const checkValue = option?.value ?? 1;
	if (Number(checkValue)) {
		return {
			key: checkValue,
			value: checkValue,
		};
	}
	return {
		key: checkValue,
		value: checkValue,
	};
};

type TPayPeriodsProps = {
	locationId: string;
	commissionCyclesPerMonth?: ICommissionCyclesPerMonthFields | null;
};

const PayPeriods = (props: TPayPeriodsProps) => {
	const { locationId, commissionCyclesPerMonth } = props;
	const user = useSelector((state: RootState) => state.user.user);
	const queryClient = useQueryClient();
	const { useAuth } = useAuthorization();
	const commissionsCyclePerMonthAction = useAuth(actions.EDIT_COMMISSIONS_CYCLES_PER_MONTH);

	const { mutate: updateCommissionCyclePerMonth, isPending: isCommissionCyclePerMonthLoading } = useMutation({
		mutationFn: (input: { value: string; lastModifiedBy: string; lastModifiedOn: Date }) =>
			updateSystemSettings(locationId, { commissionCyclesPerMonth: input }, "Commission Cycles Per Month"),
		onSuccess: (result: ISystemSettings) =>
			queryClient.setQueryData(["systemSettings", locationId], (oldData: ISystemSettings) => ({
				...oldData,
				commissionCyclesPerMonth: result.commissionCyclesPerMonth,
			})),
		onError: (error) => errorLogger(error),
	});

	const handleCommissionCycleChange = async (selected: { key: number; value: number }) => {
		const stringValue = String(selected?.value);
		if (stringValue !== commissionCyclesPerMonth?.value) {
			const input = {
				value: stringValue,
				lastModifiedBy: user?._id ?? "",
				lastModifiedOn: new Date(),
			};
			updateCommissionCyclePerMonth(input);
		}
	};

	return (
		<Grid container direction="column" className="!mt-[16px]">
			<SmartSelector
				showBy={"value"}
				selectBy={"key"}
				options={getSpecificOptions(6)}
				selected={getSelectedOption(commissionCyclesPerMonth)}
				inputProps={{
					placeholder: "Select...",
				}}
				listBoxProps={{
					sx: { maxHeight: "220px" },
				}}
				disabled={!commissionsCyclePerMonthAction || isCommissionCyclePerMonthLoading}
				sx={{ width: "150px" }}
				onChangeAction={(value) => handleCommissionCycleChange(value as { key: number; value: number })}
			/>
		</Grid>
	);
};

export default PayPeriods;
